import { animated, useSpring } from "@react-spring/three";
import { useGLTF } from "@react-three/drei";
import React, { useContext, useEffect, useMemo } from "react";
import { DoubleSide, TextureLoader } from "three";
import {
  BoltClosePos,
  BoltOpenPosBack,
  BoltOpenPosFront,
  Deg0,
  DoorDeg90CCW,
  HandleDeg45CW,
  Scale0,
  Scale1,
} from "../../utils/AnimaionSprings";
import CurrentContext from "../../utils/CurrentContext";
import useChangedTexture from "../../utils/customHooks/useChangedTexture";
import useTextureCustom from "../../utils/customHooks/useTextureCustom";
import {
  BlackPlastic,
  BoltMaterial,
  PaintMaterial,
  PocketMaterial,
} from "../../utils/finsishesData";
import { handleExportGLTF } from "../../utils/jsHelper";
import { LockSecuramBacklit, LockSecuramProLogic } from "./model_locks";
import useIsMobile from "../../utils/customHooks/useIsMobile";

//region Default options
const defaultoptions = [
  {
    materialName: "body_paint",
    finishToApply: "burgundymarble",
  },
  {
    materialName: "interior_fabric",
    finishToApply: "beigevelour",
  },
  {
    materialName: "securam_prologic_lock",
    finishToApply: "Lock_securam_prologic",
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item.materialName], item.finishToApply);
};

export function PremiumHome8({ animateInit, currentVariant }) {
  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile ? "models/premiumhome8-m.glb" : "models/premiumhome8.glb");
  const mobileModel = useGLTF("models/premiumhome8-m.glb");

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          animateInit,
          currentVariant,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            animateInit,
            currentVariant,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            animateInit,
            currentVariant,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  animateInit,
  currentVariant,
  lockType,
  visible = true,
  mobileProp = false,
}) => {
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
  } = useContext(CurrentContext);


  //region animation springs
  const { rotation_Door } = useSpring({
    rotation_Door: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg45CW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg45CW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Back } = useSpring({
    position_Bolt_Back: doorOpen ? BoltOpenPosBack : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: "premiumhome8",
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR,mobileProp]);

  const loader = useMemo(() => new TextureLoader(), []);

  const decalDoorLogo = useMemo(
    () => loader.load("textures/decal_door_premiumhome8.png"),
    []
  );

  defaultoptions.map((item) => {
    HandleUpdateTexture(item, materials);
  });

  const materialNames = {
    interior_materials: "interior_fabric",
    outer_materials: "body_paint",
    hardware_materials: ["handle", "securam_prologic_lock"],
  };

  useChangedTexture(
    currentVariant,
    materials,
    materialNames,
    HandleUpdateTexture
  );
  useTextureCustom(PaintMaterial, currentVariant.outer);

  const decalOuterColor =
    currentVariant.hardware === "blackchromehardware"
      ? "#515151"
      : currentVariant.hardware === "chromehardware"
      ? "#e5e5e5"
      : currentVariant.hardware === "brasshardware"
      ? "#C1A163"
      : "#515151";

      const poketOutline =
        currentVariant.interior === "beigevelour"
          ? "#9B784B"
          : currentVariant.interior === "silvervelour"
          ? "#64615D"
          : "#9B784B";
    
      PocketMaterial.color.set(poketOutline);
      materials.Door_Bag_Decal.color.set(poketOutline);

  return (
    <group dispose={null} visible={visible}>
      <animated.group
        position={[0.242, 0.069, 0.2175]}
        rotation={rotation_Door}
      >
        <animated.mesh
          position={position_Bolt_Front}
          castShadow
          geometry={nodes.bolts_front.geometry}
          material={BoltMaterial}
        />
        <mesh
          castShadow
          geometry={nodes.bolts_housing.geometry}
          material={BlackPlastic}
        />
        <mesh
          castShadow
          geometry={nodes.door_interior_bar_top.geometry}
          material={materials.Outlet}
        />

        <animated.group
          position={[-0.243, 0.333, -0.013]}
          rotation={handle_Rotation1}
          scale={handle_Scale1}
        >
          <mesh
            position={[0, 0, 0.005]}
            castShadow
            receiveShadow
            geometry={nodes.handle_1.geometry}
            material={materials.Handle_Plane}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handle_2.geometry}
            material={materials.handle}
          />
        </animated.group>
        <animated.group
          position={[-0.243, 0.333, -0.013]}
          rotation={handle_Rotation2}
          scale={handle_Scale2}
        >
          <mesh
            position={[0, 0, 0.005]}
            castShadow
            receiveShadow
            geometry={nodes.handle_1.geometry}
            material={materials.Handle_Plane}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handle_2.geometry}
            material={materials.handle}
          />
        </animated.group>

        {/* Locks */}
        <group position={[-0.24, 0.455, 0.024]}>
          {/* <LockSecuramProLogic material={materials.securam_prologic_lock} /> */}
           <LockSecuramBacklit material={materials.handle} />
        </group>
        {/* Door Exterior */}
        <group>
          <mesh
            castShadow
            geometry={nodes.outer_door.geometry}
            material={PaintMaterial}
          ></mesh>
          <mesh position={[-0.2475, 0.3025, 0.03]} scale={[1.7, 1.7, 1]}>
            <planeGeometry args={[0.7, 0.7]} />
            <meshBasicMaterial
              color={decalOuterColor}
              map={decalDoorLogo}
              transparent={true}
              //side={DoubleSide}
            />
          </mesh>
        </group>

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_1.geometry}
          material={materials.Pockets_Door}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_2.geometry}
          material={materials.Door_Hook_Base}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_3.geometry}
          material={materials.Outlet}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_4.geometry}
          material={materials.Bars}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_5.geometry}
          material={PocketMaterial}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_6.geometry}
          material={materials.Door_Bag_Decal}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pocket_base_7.geometry}
          material={materials.Door_Hook}
        />
      </animated.group>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior.geometry}
        material={materials.interior_fabric}
      />
      <mesh
        castShadow
        geometry={nodes.outer_body.geometry}
        material={PaintMaterial}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_1.geometry}
        material={materials.Outlet}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_2.geometry}
        material={materials.Bars}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_3.geometry}
        material={materials.Safe_Interior_HingeBox}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_4.geometry}
        material={BlackPlastic}
      />
    </group>
  );
}

//useGLTF.preload("models/premiumhome8.glb");
